import React from 'react';
import {useViewport} from './Viewport';
import {useOverlay} from './OverlayContext';

type SpringModalContext = boolean;
const SpringModalContext = React.createContext<SpringModalContext>(false);

/**
 * This is in the Overlay system to stop circular dependencies between
 * spring/components, spring/hooks, and other tools.
 */
export const RegisterModalWithOverlay = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const isNested = React.useContext(SpringModalContext);
  if (isNested) {
    return children;
  } else {
    return (
      <SpringModalContext.Provider value>
        {children}
      </SpringModalContext.Provider>
    );
  }
};

export const useIsWithinSpringModal = () => {
  return React.useContext(SpringModalContext);
};

/**
 * If we are using the new Spring Modal system, then we will need to have elements placed by the legacy Positioner system escape the inherited Viewport.
 * Previously, this was handled by the Modal system, but since we are no longer using the Positioner to place floating elements, we did not build it into the new Modal.
 *
 * As such, we need to escape the viewport here until we deprecate these legacy systems.
 */
export const useSpringModalViewport = () => {
  const inheritedViewport = useViewport();
  const overlayContext = useOverlay();
  const overlayViewport = overlayContext?.[0]?.box;
  const isWithinSpringModal = React.useContext(SpringModalContext);

  const viewport =
    isWithinSpringModal && overlayViewport
      ? overlayViewport
      : inheritedViewport;

  return React.useMemo(() => viewport, [viewport]);
};
